import { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { captureException, SentrySources } from 'utils/sentry'
import { getErrorInstance } from 'utils/helpers'
import { AdapterError, AdapterOptions, AssessmentAdapter } from './types'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import {
  flattenQuestionnaireResponseItem,
  rebuildResponseFromFlat
} from '../../Patients/Assessments/CreateAssessment/helpers'

export abstract class BaseAssessmentAdapter<TFormData = Record<string, unknown>>
  implements AssessmentAdapter<TFormData>
{
  abstract readonly assessmentType: AssessmentType

  toForm(questionnaireResponse: QuestionnaireResponse, options?: AdapterOptions): TFormData {
    try {
      // Convert FHIR → flat structure
      const flatAnswers = flattenQuestionnaireResponseItem(questionnaireResponse.item ?? [])

      // Direct conversion from flatAnswers to typed form data
      return this.flatAnswersToFormData(flatAnswers, options)
    } catch (error) {
      throw new AdapterError(
        `Failed to convert QuestionnaireResponse to form data for ${this.assessmentType}`,
        this.assessmentType,
        undefined,
        error instanceof Error ? error : new Error(String(error))
      )
    }
  }

  toFHIR(
    formData: TFormData,
    existingFhir: QuestionnaireResponse,
    options?: AdapterOptions
  ): QuestionnaireResponse {
    try {
      // Get current flat structure
      const currentFlatAnswers = flattenQuestionnaireResponseItem(existingFhir.item ?? [])
      // Apply validation first
      this.validateFormData(formData)
      // Direct conversion from typed form data to flatAnswers
      const updatedFlatAnswers = this.formDataToFlatAnswers(formData, currentFlatAnswers, options)
      // Convert flat → FHIR nested structure
      const rebuiltItems = rebuildResponseFromFlat(updatedFlatAnswers)

      return {
        ...existingFhir,
        item: rebuiltItems,
        authored: new Date().toISOString()
      }
    } catch (error) {
      captureException(getErrorInstance(error), {
        tags: { source: SentrySources.ASSESSMENT_ADAPTER },
        extras: {
          params: {
            adapterName: this.constructor.name,
            assessmentType: this.assessmentType
          }
        }
      })
      throw new AdapterError(
        `Failed to convert form data to QuestionnaireResponse for ${this.assessmentType}`,
        this.assessmentType,
        this.constructor.name,
        getErrorInstance(error)
      )
    }
  }

  /**
   * Override this method to convert flatAnswers directly to typed form data
   */
  protected abstract flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): TFormData

  /**
   * Override this method to convert typed form data directly to flatAnswers
   */
  protected abstract formDataToFlatAnswers(
    formData: TFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem>

  /**
   * Override this method for assessment-specific validation
   */
  protected validateFormData(_formData: TFormData): void {
    // Override in subclasses for assessment-specific validation
  }
}
